import React, { useContext, useRef } from 'react';
import { TaskContext } from '../contexts/TaskContext';
import { TimerContext } from '../contexts/TimerContext';
import { Button, Box, Typography, Alert, Snackbar } from '@mui/material';
import { Download, Upload } from '@mui/icons-material';

const ImportExport = () => {
  const { exportTasks, importTasks } = useContext(TaskContext);
  const { exportAllData, importLogs } = useContext(TimerContext);
  const fileInputRef = useRef(null);
  const [alert, setAlert] = React.useState({ open: false, message: '', severity: 'info' });

  const handleExportTasks = () => {
    const data = exportTasks();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `time-logger-tasks-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setAlert({ open: true, message: 'Tasks exported successfully!', severity: 'success' });
  };

  const handleExportAllData = () => {
    const tasksData = exportTasks();
    const timerData = exportAllData();
    const combinedData = {
      ...tasksData,
      ...timerData,
      exportType: 'complete',
    };
    const blob = new Blob([JSON.stringify(combinedData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `time-logger-complete-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setAlert({ open: true, message: 'All data exported successfully!', severity: 'success' });
  };

  const handleImport = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        
        if (data.tasks) {
          importTasks(data.tasks);
        }
        
        if (data.logs) {
          importLogs(data.logs);
        }
        
        setAlert({ open: true, message: 'Data imported successfully!', severity: 'success' });
      } catch (error) {
        setAlert({ open: true, message: 'Invalid file format!', severity: 'error' });
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Import/Export Data
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={handleExportTasks}
          color="primary"
        >
          Export Tasks Only
        </Button>
        
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={handleExportAllData}
          color="secondary"
        >
          Export All Data
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<Upload />}
          onClick={triggerFileInput}
        >
          Import Data
        </Button>
      </Box>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleImport}
        accept=".json"
        style={{ display: 'none' }}
      />

      <Typography variant="body2" color="textSecondary">
        Export your tasks and logs to backup your data, or import previously exported data.
        The complete export includes tasks, logs, and timer settings.
      </Typography>

      <Snackbar 
        open={alert.open} 
        autoHideDuration={3000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ImportExport;